import { CreateShiftInput, UpdateShiftInput } from "./../models/GQL_API";
import { ListingByConceptVariables, Option } from "../models/app";
import { GRAPHQL_AUTH_MODE } from "@aws-amplify/api";
import { API } from "aws-amplify";
import { GraphQLQuery } from "@aws-amplify/api";
import { useDispatch, useSelector } from "react-redux";
import useApp from "./useApp";
import { Shift } from "../models";
import { createShift, updateShift } from "../graphql/mutations";
import { getShift, listShifts } from "../graphql/queries";
import { HeadCell } from "../models/dataTable";
import {
  setLastIndex,
  setListing,
  setNextToken,
  setPagination,
  setAllListing,
  setUserShifts,
  setSelectedFilters,
  setFilters,
} from "../store/ducks/shift";
import { getDateFormatted } from "../helpers/utils";
import { onCreateShift } from "../graphql/subscriptions";

const useShift = (listingName: string, singleName: string) => {
  const dispatch = useDispatch();
  const { showError, showConfirm } = useApp();

  const session = useSelector((state: any) => state.app.session);
  const shiftsAllListing = useSelector((state: any) => state.shifts.allListing);

  const nextToken = useSelector((state: any) => state.shifts.nextToken);
  const lastIndex = useSelector((state: any) => state.shifts.lastIndex);
  const paginationListing = useSelector(
    (state: any) => state.shifts.pagination
  );
  const accountsSelected = useSelector((state: any) => state.accounts.selected);

  async function fetch(params: any) {
    try {
      const { accountID, searchText, limit, startIndex } = params;
      const filter: any = {
        accountID: { eq: accountID ? accountID : accountsSelected.id },
      };
      if (searchText.length > 0) {
        filter.name = { contains: searchText.toLowerCase() };

        const listing: any = await API.graphql<GraphQLQuery<Shift>>({
          query: listShifts,
          variables: { filter, limit: 1000 },
          authMode: session
            ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
            : GRAPHQL_AUTH_MODE.AWS_IAM,
        });
        dispatch(setLastIndex(0));
        dispatch(setNextToken(null));
        dispatch(setPagination([]));

        return listing.data.listShifts.items;
      }
      if (
        (lastIndex >= startIndex &&
          lastIndex !== null &&
          paginationListing.length > 0 &&
          accountsSelected.id === paginationListing[0].accountID &&
          limit < paginationListing.length) ||
        (paginationListing.length > 0 && !nextToken)
      ) {
        return paginationListing.slice(startIndex, startIndex + limit);
      }
      const listing: any = await API.graphql<GraphQLQuery<Shift>>({
        query: listShifts,
        variables: {
          filter,
          limit: limit ?? 1000,
          nextToken:
            paginationListing.length > 0 &&
            accountsSelected.id === paginationListing[0]?.accountID
              ? nextToken
              : undefined,
        },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });
      dispatch(setLastIndex(startIndex));
      dispatch(setNextToken(listing.data.listShifts.nextToken));
      let list = paginationListing.concat(listing.data.listShifts.items);
      dispatch(setPagination(list));

      return list.slice(startIndex, startIndex + limit);
    } catch (err) {
      showError(err);
    }
  }

  async function fetchAll(params: any) {
    try {
      const { accountID, searchText, limit } = params;
      const filter: any = {
        accountID: { eq: accountID ? accountID : accountsSelected.id },
        deleted: { eq: "0" },
      };

      if (searchText && searchText.length > 0) {
        filter.name = { contains: searchText.toLowerCase() };
      }

      if (
        shiftsAllListing.length === 0 ||
        shiftsAllListing.id !== shiftsAllListing[0].accountID
      ) {
        const listing: any = await API.graphql<GraphQLQuery<Shift>>({
          query: listShifts,
          variables: { filter, limit: limit ?? 1000 },
          authMode: session
            ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
            : GRAPHQL_AUTH_MODE.AWS_IAM,
        });
        return listing.data.listShifts.items;
      } else {
        return shiftsAllListing;
      }
    } catch (err) {
      console.log(err);
      showError(err);
    }
  }

  async function fetchByUser(params: any) {
    try {
      const { userID, accountID, limit } = params;
      const filter: any = {};
      filter.accountID = { eq: accountID ? accountID : accountsSelected.id };

      const listing: any = await API.graphql<GraphQLQuery<Shift>>({
        query: listShifts,
        variables: { filter, limit: limit ?? 1000 },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      let list: any = [];
      for (let i = 0; i < listing.data.listShifts.items.length; i++) {
        if (listing.data.listShifts.items[i].users.includes(userID)) {
          list.push(listing.data.listShifts.items[i]);
        }
      }
      return list;
    } catch (err) {
      showError(err);
    }
  }

  async function get(params: any) {
    const { id } = params;

    try {
      const shift: any = await API.graphql({
        query: getShift,
        variables: { id },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      return shift.data.getShift;
    } catch (err) {
      throw err;
    }
  }

  async function create(params: any, session = true) {
    let { userID, userName, data } = params;

    try {
      const createInput: CreateShiftInput = {
        name: data.name.toLowerCase(),
        precedence: data.precedence ? data.precedence : "0",
        startTime: data.startTime ? data.startTime : null,
        endTime: data.endTime ? data.endTime : null,
        accountID: data.accountID,
        users: data.users ? data.users : [],
        createdAt: new Date().toISOString(),
        createdByID: userID,
        createdByName: userName,
      };

      const shift = await API.graphql<GraphQLQuery<Shift>>({
        query: createShift,
        variables: { input: createInput },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      dispatch(setLastIndex(0));
      dispatch(setNextToken(null));
      dispatch(setPagination([]));
      showConfirm(`New ${singleName} has been created successfully`);

      return shift;
    } catch (err) {
      console.log(err);
    }
  }

  async function update(params: any, session = false) {
    try {
      const { data } = params;
      let original = await get(params);

      const updateInput: UpdateShiftInput = {
        id: original.id,
        name: data.name ? data.name.toLowerCase() : original.name,
        startTime: data.startTime ? data.startTime : original.startTime,
        endTime: data.endTime ? data.endTime : original.endTime,
        users: data.users ? data.users : original.users,
        precedence: data.precedence ? data.precedence : original.precedence,
        _version: original._version,
      };

      const Shift: any = await API.graphql<GraphQLQuery<Shift>>({
        query: updateShift,
        variables: { input: updateInput },
        authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
      });

      dispatch(setLastIndex(0));
      dispatch(setNextToken(null));
      dispatch(setPagination([]));
      showConfirm(`${singleName} has been updated successfully`);

      return Shift.data.updateShift;
    } catch (err) {
      showError(err);
    }
  }

  async function exportAll(params: ListingByConceptVariables) {
    const data = await fetch(params);
    return data;
  }

  function options(listing: Shift[]) {
    const options: Option[] = [];

    for (let option of listing) {
      options.push({ label: option.name, value: option.id });
    }

    return options;
  }

  const headCells: readonly HeadCell[] = [
    {
      id: "name",
      numeric: false,
      disablePadding: false,
      label: "Name",
    },
    {
      id: "precedence",
      numeric: false,
      disablePadding: false,
      label: "Precedence",
    },
    {
      id: "startTime",
      numeric: false,
      disablePadding: false,
      label: "Start Time",
    },
    {
      id: "endTime",
      numeric: false,
      disablePadding: false,
      label: "End Time",
    },
    {
      id: "createdBy",
      numeric: false,
      disablePadding: false,
      label: "Created By",
    },
    {
      id: "createdAt",
      numeric: false,
      disablePadding: false,
      label: "Date",
    },
  ];
  const dataCells: readonly string[] = [
    "name",
    "precedence",
    "startTime",
    "endTime",
  ];

  const api: any = {};

  api[`${listingName}Model`] = Shift as any;
  api[`${listingName}CreateSubscription`] = onCreateShift;

  api[`${listingName}HeadCells`] = headCells;
  api[`${listingName}DataCells`] = dataCells;
  api[`${listingName}Fetch`] = fetch;
  api[`${listingName}FetchAll`] = fetchAll;
  api[`${listingName}FetchByUser`] = fetchByUser;
  api[`${listingName}Options`] = options;
  api[`${listingName}Get`] = get;
  api[`${listingName}Create`] = create;
  api[`${listingName}Update`] = update;
  api[`${listingName}Export`] = exportAll;
  api[`${listingName}ChangeUserShifts`] = (listing: Shift[]) =>
    dispatch(setUserShifts(listing));
  api[`${listingName}ChangeListing`] = (listing: Shift[]) => {
    dispatch(setListing(listing));
    dispatch(setFilters(listing.map((model: any) => model.name)));
  };
  api[`${listingName}ChangeAllListing`] = (listing: Shift[]) => {
    dispatch(setAllListing(listing));
    dispatch(setFilters(listing.map((model: any) => model.name)));
  };
  api[`${listingName}ChangeSelectedFilters`] = (filters: any) => {
    dispatch(setSelectedFilters(filters));
  };

  return api;
};

export default useShift;
